import React, { useState, useMemo } from 'react';
import './App.module.css';
import { Layout, Spin } from 'antd';
import { AppLayout, SalesforceLogin } from './components';
import { UserContext } from './components/Reducers/UserContext';
import createPersistedState from 'use-persisted-state';
import { ApiQueryClientProvider } from './api/apiQueryClientProvider.js';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Auth0ProviderWrapper from './components/Auth0Login/Auth0ProviderWrapper.jsx';
import { SecureConfigProvider, useSecureConfigContext } from './components/SecureConfig/SecureConfigProvider.jsx';

const useAppState = createPersistedState('data');

const { Header } = Layout;

// Inner component that has access to SecureConfigContext
const AppContent = ({ isLoggedIn, setLogger }) => {
    const { loading, error, isConfigReady } = useSecureConfigContext();

    // For non-authenticated users, proceed immediately
    if (!isLoggedIn.active) {
        return (
            <Auth0ProviderWrapper>
                <SalesforceLogin />
            </Auth0ProviderWrapper>
        );
    }

    // For authenticated users: Wait for secure config to be ready to avoid API call errors
    if (!isConfigReady) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    flexDirection: 'column',
                }}
            >
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                    {loading ? 'Loading secure configuration...' : 'Initializing secure configuration...'}
                </div>
                {error && <div style={{ color: 'red', marginTop: 8, fontSize: '12px' }}>Error: {error}</div>}
            </div>
        );
    }

    // Only render AppLayout when secure config is confirmed ready
    return (
        <Auth0ProviderWrapper>
            <AppLayout />
        </Auth0ProviderWrapper>
    );
};

function App() {
    const [isLoggedIn, setLogger] = useAppState({
        active: false,
        user: {
            role: 'guest',
            name: 'Mansoor',
            auth0LogOut: false,
        },
    });

    const value = useMemo(() => ({ isLoggedIn, setLogger }), [isLoggedIn, setLogger]);

    // Get user identifier for secure config (you can modify this based on your user management)
    const getUserIdentifier = () => {
        if (isLoggedIn.active && isLoggedIn.user?.name) {
            return isLoggedIn.user.name;
        }
        return 'default'; // fallback for non-authenticated users
    };

    return (
        <ApiQueryClientProvider>
            <SecureConfigProvider userIdentifier={getUserIdentifier()}>
                <UserContext.Provider value={value}>
                    <ReactQueryDevtools initialIsOpen={false} />
                    <AppContent isLoggedIn={isLoggedIn} setLogger={setLogger} />
                </UserContext.Provider>
            </SecureConfigProvider>
        </ApiQueryClientProvider>
    );
}

export default App;
